import React, { useMemo, useState } from 'react';

import { Checkbox } from '@buildoutinc/blueprint-react/ui/Checkbox';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Select } from '@buildoutinc/blueprint-react/ui/Select';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { put } from 'helpers/api';
import CompanyReminders from 'bundles/apex/reminders/company';

import type {
  NotificationOption,
  NotificationParamKey,
  NotificationPreferences,
  SelectOption,
} from 'components/notification_preferences/types';

interface Props {
  notificationPreferences: NotificationPreferences;
  paramKey: NotificationParamKey;
  updatePath: string;
  companyReminders?: Record<string, unknown> | null;
}

const DEAL_STAGE_MY = 'deal_stage_updated_my_deals';
const DEAL_STAGE_ALL = 'deal_stage_updated_all_deals';

function inputId(name: string) {
  return `notification-preference-${name.replace(/[^\w]+/g, '-')}`;
}

function complexPreferenceKey(name: string) {
  return name.match(/\[(\w+)\]/)?.[1] ?? null;
}

function selectItems(options: SelectOption[]): Record<string, string> {
  return options.reduce<Record<string, string>>((items, [label, value]) => {
    items[value] = label;
    return items;
  }, {});
}

export default function Notifications({
  notificationPreferences,
  paramKey,
  updatePath,
  companyReminders = null,
}: Props) {
  const { sections, timeOptions } = notificationPreferences;
  const isCompany = paramKey === 'company';
  const hasPreferences = sections.length > 0;
  const timeItems = useMemo(() => selectItems(timeOptions), [timeOptions]);

  const allOptions = useMemo(
    () => sections.reduce<NotificationOption[]>((options, section) => options.concat(section.options), []),
    [sections],
  );

  const { membersByGroup, parentByGroup } = useMemo(() => {
    const members: Record<string, string[]> = {};
    const parents: Record<string, string> = {};

    allOptions.forEach((option) => {
      if (option.formGroup) {
        members[option.formGroup] = [...(members[option.formGroup] ?? []), option.name];
      }
      if (option.toggleGroup) {
        parents[option.toggleGroup] = option.name;
      }
    });

    return { membersByGroup: members, parentByGroup: parents };
  }, [allOptions]);

  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    allOptions.forEach((option) => {
      initial[option.name] = option.value;
    });
    return initial;
  });

  const [subOptionValues, setSubOptionValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    allOptions.forEach((option) => {
      if (option.subOption) initial[option.subOption.name] = option.subOption.value ?? '';
    });
    return initial;
  });

  const [scheduleValues, setScheduleValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    allOptions.forEach((option) => {
      if (option.schedulable && option.scheduleName) initial[option.scheduleName] = option.scheduleValue ?? '';
    });
    return initial;
  });

  const [saving, setSaving] = useState(false);

  function isDisabled(option: NotificationOption) {
    if (option.locked) return true;
    if (option.name === DEAL_STAGE_MY) return !!checkboxValues[DEAL_STAGE_ALL];
    if (option.name === DEAL_STAGE_ALL) return !!checkboxValues[DEAL_STAGE_MY];
    return false;
  }

  function handleCheckboxChange(option: NotificationOption, checked: boolean) {
    setCheckboxValues((prev) => {
      const next = { ...prev, [option.name]: checked };

      if (option.toggleGroup) {
        (membersByGroup[option.toggleGroup] ?? []).forEach((member) => {
          next[member] = checked;
        });
      } else if (option.formGroup) {
        const parent = parentByGroup[option.formGroup];
        if (parent) {
          next[parent] = (membersByGroup[option.formGroup] ?? []).every(member => next[member]);
        }
      }

      return next;
    });
  }

  function handleSubOptionChange(name: string, value: string) {
    setSubOptionValues(prev => ({ ...prev, [name]: value }));
  }

  function handleScheduleChange(name: string, value: string) {
    setScheduleValues(prev => ({ ...prev, [name]: value }));
  }

  function buildPreferenceAttributes() {
    const attributes: Record<string, unknown> = {};
    const syndicationPartners: Record<string, boolean> = {};

    allOptions.forEach((option) => {
      const complexKey = option.formGroup ? complexPreferenceKey(option.name) : null;

      if (complexKey) {
        syndicationPartners[complexKey] = checkboxValues[option.name];
      } else {
        attributes[option.name] = checkboxValues[option.name];
      }

      if (option.subOption) {
        attributes[option.subOption.name] = subOptionValues[option.subOption.name];
      }
      if (option.schedulable && option.scheduleName) {
        attributes[option.scheduleName] = scheduleValues[option.scheduleName];
      }
    });

    if (Object.keys(syndicationPartners).length > 0) {
      attributes.syndication_partners_notification = syndicationPartners;
    }

    return attributes;
  }

  async function handleSave() {
    setSaving(true);

    try {
      await put(updatePath, { [paramKey]: { notification_preference_attributes: buildPreferenceAttributes() } });
      showSnackbar({ text: 'Your notification preferences were saved.', variant: 'success' });
    } catch {
      showSnackbar({ text: 'Something went wrong saving your preferences.', variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {companyReminders && <CompanyReminders {...companyReminders} />}
      {companyReminders && hasPreferences && <hr />}

      {hasPreferences && (
        <>
          {isCompany ? (
            <>
              <h6 className="mt-7">Default Notification Preferences</h6>
              <p className="text-muted mb-2">
                When a new user is added, control what their default notifications preferences will be
              </p>
            </>
          ) : (
            <h5 className="mb-4">Notification Preferences</h5>
          )}

          <div className="d-flex flex-column gap-8">
            {sections.map(section => (
              <div key={section.title}>
                <div className="mb-2">
                  <div className="fs-large text-storm-grey-800">
                    <i className={`fas fa-lg me-2 ${section.icon}`} />
                    <span>{section.title}</span>
                  </div>
                </div>

                <List>
                  {section.options.map((option) => {
                    const rowPadding = option.subOption || option.schedulable ? '' : 'py-3';

                    const checkbox = (
                      <Checkbox
                        checked={checkboxValues[option.name] ?? false}
                        disabled={isDisabled(option)}
                        id={inputId(option.name)}
                        onCheckedChange={checked => handleCheckboxChange(option, checked === true)}
                      />
                    );

                    return (
                      <List.Item className={`d-flex align-items-center px-3 ${rowPadding}`} key={option.name}>
                        {option.locked ? (
                          <Tooltip>
                            <Tooltip.Trigger delay={0} render={<div>{checkbox}</div>} />
                            <Tooltip.Content>{option.locked}</Tooltip.Content>
                          </Tooltip>
                        ) : (
                          <div>{checkbox}</div>
                        )}

                        <div className="flex-grow-1">
                          <Label htmlFor={inputId(option.name)}>{option.text}</Label>
                        </div>

                        {option.subOption && (
                          <div className="d-flex align-items-center">
                            {option.subOption.label && (
                              <Label className="fw-semibold" htmlFor={inputId(option.subOption.name)}>
                                {option.subOption.label}
                              </Label>
                            )}
                            <Select
                              items={selectItems(option.subOption.options)}
                              value={subOptionValues[option.subOption.name] ?? ''}
                              onValueChange={value => handleSubOptionChange(option.subOption!.name, value ?? '')}
                            >
                              <Select.Trigger
                                className="ms-2 gap-2"
                                id={inputId(option.subOption.name)}
                                style={{ width: 'auto', minWidth: '10rem' }}
                              >
                                <Select.Value />
                              </Select.Trigger>
                              <Select.Content>
                                {option.subOption.options.map(([label, value]) => (
                                  <Select.Item key={value} value={value}>{label}</Select.Item>
                                ))}
                              </Select.Content>
                            </Select>
                          </div>
                        )}

                        {option.schedulable && option.scheduleName && (
                          <div className="d-flex align-items-center">
                            <Label className="fw-semibold" htmlFor={inputId(option.scheduleName)}>
                              Time:
                            </Label>
                            <Select
                              items={timeItems}
                              value={scheduleValues[option.scheduleName] ?? ''}
                              onValueChange={value => handleScheduleChange(option.scheduleName!, value ?? '')}
                            >
                              <Select.Trigger
                                className="ms-2 gap-2"
                                id={inputId(option.scheduleName)}
                                style={{ width: 'auto', minWidth: '7rem' }}
                              >
                                <Select.Value />
                              </Select.Trigger>
                              <Select.Content>
                                {timeOptions.map(([label, value]) => (
                                  <Select.Item key={value} value={value}>{label}</Select.Item>
                                ))}
                              </Select.Content>
                            </Select>
                          </div>
                        )}
                      </List.Item>
                    );
                  })}
                </List>
              </div>
            ))}
          </div>

          <div className="text-end mt-4">
            <Button disabled={saving} label="Save Changes" onClick={handleSave} />
          </div>
        </>
      )}
    </div>
  );
}
